import React, { useCallback } from "react";
import { Text, TouchableOpacity, View } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useTranslation } from "react-i18next";

import Colors from "../../../../Resources/Color";
import {
  resolveClientAthleteDisplayName,
  buildClientNutritionCopyAthleteResults,
} from "./clientNutritionCopyToAthleteUtils";
import ClientNutritionSelectionModal, {
  clientNutritionSelectionModalStyles as styles,
} from "./ClientNutritionSelectionModal";
import { getNutritionPlanDisplayName } from "../nutritionPlanNameUtils";
import PendingInvitationIndicator, {
  PENDING_INVITATION_INDICATOR_VARIANT,
} from "../../../invitations/PendingInvitationIndicator";

function AthleteResultRow({
  athlete,
  isHovered,
  isSelected,
  isMobile,
  onHover,
  onPress,
  onLayout,
}) {
  return (
    <TouchableOpacity
      activeOpacity={0.96}
      onLayout={onLayout}
      onMouseEnter={!isMobile ? onHover : undefined}
      onPress={onPress}
      style={styles.resultRowTouchable}
    >
      <View
        style={[
          styles.resultRow,
          isHovered && !isSelected ? styles.resultRowHover : null,
          isSelected ? styles.resultRowSelected : null,
        ]}
      >
        <View style={styles.resultMain}>
          <View style={styles.resultIconWrap}>
            <Ionicons name="person-outline" size={16} color={Colors.grayColor} />
          </View>
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              minWidth: 0,
              flexShrink: 1,
            }}
          >
            <Text numberOfLines={1} style={styles.resultName}>
              {resolveClientAthleteDisplayName(athlete)}
            </Text>
            <PendingInvitationIndicator
              client={athlete}
              variant={PENDING_INVITATION_INDICATOR_VARIANT.ICON_ONLY}
            />
          </View>
        </View>
      </View>
    </TouchableOpacity>
  );
}

export default function ClientNutritionPlanCopyToAthleteModal({
  athletes = [],
  currentClientId = null,
  isVisible = false,
  onClose,
  onSelectAthlete,
  sourcePlan = null,
}) {
  const { i18n, t } = useTranslation();
  const language = i18n?.resolvedLanguage || i18n?.language || "en";
  const sourcePlanName = getNutritionPlanDisplayName(sourcePlan, language);
  const buildSearchResults = useCallback(
    (sourceAthletes, searchText) =>
      buildClientNutritionCopyAthleteResults(
        sourceAthletes,
        searchText,
        currentClientId,
      ),
    [currentClientId],
  );

  return (
    <ClientNutritionSelectionModal
      badgeIconName="reader-outline"
      badgeText={sourcePlanName}
      buildSearchResults={buildSearchResults}
      eyebrowText={t(
        "clientNutritionPlanCopyToAthleteEyebrow-string",
        "Copy to athlete",
      )}
      emptyState={({ items }) => {
        const hasAthletes = buildClientNutritionCopyAthleteResults(
          items,
          "",
          currentClientId,
        ).length > 0;

        return {
          iconName: hasAthletes ? "search-outline" : "person-outline",
          mainText: hasAthletes
            ? "nutritionPlanAssignmentsNoAthletesFound-string"
            : "clientNutritionPlanCopyNoAthletesTitle-string",
          subText: hasAthletes
            ? "adjustFiltersOrSearch-string"
            : "clientNutritionPlanCopyNoAthletesDescription-string",
        };
      }}
      isVisible={isVisible}
      items={athletes}
      loadingTitle={t("nutritionPlanAssignmentsLoadingAthletes-string", "Loading athletes")}
      onClose={onClose}
      onSelectItem={onSelectAthlete}
      renderItemRow={({ item, ...rowProps }) => (
        <AthleteResultRow
          key={String(item?.id ?? rowProps.index)}
          athlete={item}
          {...rowProps}
        />
      )}
      searchAccessibilityLabel={t(
        "nutritionPlanAssignmentsSearchPlaceholder-string",
        "Search athletes",
      )}
      searchPlaceholder={t(
        "nutritionPlanAssignmentsSearchPlaceholder-string",
        "Search athletes",
      )}
    />
  );
}
